import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Text,
  Card,
  Group,
  Badge,
  Box,
  Loader,
  Center,
  Button,
  Stack,
  Avatar,
  Grid,
  SimpleGrid,
  ActionIcon,
  FileButton,
  Modal,
  Timeline,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  ArrowLeft,
  Edit,
  MapPin,
  Clock,
  Upload,
  Mail,
  Video,
  Monitor,
  Globe,
  ExternalLink,
  Lock,
  CheckCircle,
} from 'react-feather';
import { coachesApi } from '../../services/api/coaches';
import type { Coach, CoachCohortAssignment, CoachActivityLog } from '../../types';
import { PLATFORM_FIELD } from '../../components/coaches/meetingLinkPrompt';
import { formatDateOnly, formatDateTime } from '../../utils/formatters';
import { getAvatarColor } from '../../utils/avatarHelpers';
import { RichTextContent, CoachStatusPill } from '../../components/common';
import { medleyEmailOf } from '../../utils/medleyEmail';
import {
  NO_MEDLEY_EMAIL_BADGE_STYLES,
  NO_MEDLEY_EMAIL_LABEL,
} from '../../components/coaches/noMedleyEmailBadge';
import classes from './CoachDetailPage.module.css';

// Section components
import { BasicInfoSection } from './components/BasicInfoSection';

// Edit modals
import { EditLocationTimezoneModal } from './components/EditLocationTimezoneModal';
import { EditIdentityModal } from './components/EditIdentityModal';
import { EditProfessionalModal } from './components/EditProfessionalModal';
import { EditLearningModal } from './components/EditLearningModal';
import { EditNotesModal } from './components/EditNotesModal';
import { EditSessionSettingsModal } from './components/EditSessionSettingsModal';
import { ConnectionsCard } from './components/ConnectionsCard';

export function CoachDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [coach, setCoach] = useState<Coach | null>(null);
  // MED-1355: the header badge raises this to open Basic Information edit.
  const [editMedleyEmailRequest, setEditMedleyEmailRequest] = useState(0);
  const [cohorts, setCohorts] = useState<{
    current: CoachCohortAssignment[];
    past: CoachCohortAssignment[];
  }>({ current: [], past: [] });
  const [activityLog, setActivityLog] = useState<CoachActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Modal states
  const [locationOpened, { open: openLocation, close: closeLocation }] = useDisclosure(false);
  const [identityOpened, { open: openIdentity, close: closeIdentity }] = useDisclosure(false);
  const [professionalOpened, { open: openProfessional, close: closeProfessional }] = useDisclosure(false);
  const [learningOpened, { open: openLearning, close: closeLearning }] = useDisclosure(false);
  const [sessionSettingsOpened, { open: openSessionSettings, close: closeSessionSettings }] = useDisclosure(false);
  const [inactiveConfirmOpened, { open: openInactiveConfirm, close: closeInactiveConfirm }] = useDisclosure(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [notesOpened, { open: openNotes, close: closeNotes }] = useDisclosure(false);

  // Fetch coach
  const fetchCoach = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await coachesApi.getById(id);
      setCoach(data);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to fetch coach',
        color: 'red',
      });
      navigate('/coaches');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  // Fetch cohorts
  const fetchCohorts = useCallback(async () => {
    if (!id) return;
    try {
      const data = await coachesApi.getCohorts(id);
      setCohorts(data);
    } catch (error) {
      console.error('Failed to fetch cohorts:', error);
    }
  }, [id]);

  // Fetch activity log
  const fetchActivityLog = useCallback(async () => {
    if (!id) return;
    try {
      const data = await coachesApi.getActivityLog(id);
      setActivityLog(data);
    } catch (error) {
      console.error('Failed to fetch activity log:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchCoach();
    fetchCohorts();
    fetchActivityLog();
  }, [fetchCoach, fetchCohorts, fetchActivityLog]);

  // Handle avatar upload
  // MED-1363: an update response has no booking health, which only the GET
  // computes. Keep the one already on screen.
  // MED-1365 QA round 6: the same for the missing meeting links, less each
  // link that the saved profile now has.
  const setUpdatedCoach = (updatedCoach: Coach) => {
    setCoach(prev => ({
      ...updatedCoach,
      calendly_booking_health:
        updatedCoach.calendly_booking_health ?? prev?.calendly_booking_health ?? null,
      missing_meeting_links: (
        updatedCoach.missing_meeting_links ??
        prev?.missing_meeting_links ??
        []
      ).filter(link => !updatedCoach[PLATFORM_FIELD[link.platform]]?.trim()),
    }));
  };

  const handleAvatarUpload = async (file: File | null) => {
    if (!file || !id) return;

    try {
      setUploadingAvatar(true);
      const updatedCoach = await coachesApi.uploadAvatar(id, file);
      setUpdatedCoach(updatedCoach);
      fetchActivityLog();
      notifications.show({
        title: 'Success',
        message: 'Avatar updated successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to upload avatar',
        color: 'red',
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Handle coach update from modals
  const handleCoachUpdate = (updatedCoach: Coach) => {
    setUpdatedCoach(updatedCoach);
    fetchActivityLog();
  };

  // MED-683: CMs toggle the manual inactive flag; status itself is derived from
  // modality_approvals + manually_inactive on the API side.
  const setManuallyInactive = async (markInactive: boolean) => {
    if (!coach) return;
    try {
      setChangingStatus(true);
      const updatedCoach = await coachesApi.update(coach.id, {
        manually_inactive: markInactive,
      });
      notifications.show({
        title: 'Success',
        message: markInactive
          ? 'Coach marked Inactive'
          : 'Coach reactivated — status will follow approved modalities',
        color: 'green',
      });
      handleCoachUpdate(updatedCoach);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update status',
        color: 'red',
      });
    } finally {
      setChangingStatus(false);
    }
  };

  const handleMarkInactiveClick = () => {
    if (!coach) return;
    if (cohorts.current.length > 0) {
      openInactiveConfirm();
      return;
    }
    void setManuallyInactive(true);
  };

  const handleConfirmInactive = async () => {
    closeInactiveConfirm();
    await setManuallyInactive(true);
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (!coach) {
    return (
      <Container size="xl" py="xl">
        <Text>Coach not found</Text>
      </Container>
    );
  }

  const fullName = [coach.first_name, coach.last_name].filter(Boolean).join(' ') || 'Unnamed Coach';

  // Format timezone for display
  const formatTimezone = (tz: string): string => {
    const tzMap: Record<string, string> = {
      'America/New_York': 'Eastern Time',
      'America/Chicago': 'Central Time',
      'America/Denver': 'Mountain Time',
      'America/Los_Angeles': 'Pacific Time',
      'America/Anchorage': 'Alaska Time',
      'Pacific/Honolulu': 'Hawaii Time',
      'Europe/London': 'GMT',
      'Europe/Paris': 'CET',
      'Europe/Berlin': 'CET',
      'Asia/Tokyo': 'JST',
      'Asia/Shanghai': 'CST',
      'Asia/Singapore': 'SGT',
      'Australia/Sydney': 'AEST',
    };
    return tzMap[tz] || tz.split('/').pop()?.replace(/_/g, ' ') || tz;
  };

  return (
    <>
      {/* Full-width header with shadow */}
      <Box className={classes.fullWidthHeader}>
        <Container size="xl" className={classes.headerContent}>
          {/* Top Row: Back + Title + Badge + Settings */}
          <Group justify="space-between" mb="sm">
            <Group gap="md">
              {/* Back Button */}
              <ActionIcon
                variant="filled"
                color="medleyGreen"
                size="lg"
                radius="xl"
                onClick={() => navigate('/coaches')}
              >
                <ArrowLeft size={18} />
              </ActionIcon>

              {/* Avatar */}
              <Box pos="relative">
                {(() => {
                  const bgColor = getAvatarColor(`${coach.first_name || ''}${coach.last_name || ''}`);
                  return (
                    <Avatar
                      src={coach.avatar_url}
                      size={56}
                      radius="50%"
                      styles={{
                        root: { backgroundColor: bgColor },
                        placeholder: { color: '#FFFFFF', backgroundColor: bgColor },
                      }}
                    >
                      {(coach.first_name?.[0] || '') + (coach.last_name?.[0] || '')}
                    </Avatar>
                  );
                })()}
                <FileButton onChange={handleAvatarUpload} accept="image/png,image/jpeg,image/webp">
                  {(props) => (
                    <ActionIcon
                      {...props}
                      size="sm"
                      radius="xl"
                      variant="filled"
                      color="gray"
                      pos="absolute"
                      bottom={0}
                      right={0}
                      loading={uploadingAvatar}
                      className={classes.avatarUploadButton}
                    >
                      <Upload size={10} />
                    </ActionIcon>
                  )}
                </FileButton>
              </Box>

              {/* Title + Badge */}
              <Group gap="sm">
                <Text className={classes.coachTitle}>{fullName}</Text>
                <CoachStatusPill
                  status={coach.status}
                  modalityApprovals={coach.modality_approvals ?? []}
                />
                {coach.coach_type === 'partner' && (
                  <Badge color="blue" variant="light" size="lg">
                    Partner Coach
                  </Badge>
                )}
                {coach.pronouns && (
                  <Text c="dimmed" size="sm">
                    ({coach.pronouns})
                  </Text>
                )}
              </Group>
            </Group>

            {/* MED-683: Reactivate shows for manually-inactive coaches; Mark Inactive only for Active coaches. Derived-inactive (no approved modalities) shows neither — CM must approve a modality first. */}
            {coach.manually_inactive ? (
              <Button
                variant="outline"
                color="medleyGreen"
                loading={changingStatus}
                onClick={() => void setManuallyInactive(false)}
              >
                Reactivate
              </Button>
            ) : coach.status === 'active' ? (
              <Button
                variant="outline"
                color="dangerRed"
                loading={changingStatus}
                onClick={handleMarkInactiveClick}
              >
                Mark Inactive
              </Button>
            ) : null}
          </Group>

          {/* Subtitle Row */}
          <Group className={classes.subtitle} ml={108}>
            {coach.coach_type === 'partner' && coach.partner_name && (
              <Group gap="xs">
                <Globe size={14} />
                <Text size="sm" fw={500}>{coach.partner_name}</Text>
              </Group>
            )}
            {/* MED-1355: the header shows the Medley email. An older account
                can sign in with a non-Medley address in users.email. That
                address is not the Medley email, so the header shows only the
                badge, the same as a coach with no users.email. Basic
                Information names the sign-in address. The badge opens Basic
                Information at Medley Email. */}
            {medleyEmailOf(coach.email) ? (
              <Group gap="xs">
                <Mail size={14} />
                <Text size="sm">{coach.email}</Text>
              </Group>
            ) : (
              <Badge
                component="button"
                type="button"
                onClick={() => setEditMedleyEmailRequest((count) => count + 1)}
                styles={NO_MEDLEY_EMAIL_BADGE_STYLES}
                style={{ cursor: 'pointer' }}
                title="Add a Medley email in Basic Information"
                aria-label="No Medley email. Add one in Basic Information."
              >
                {NO_MEDLEY_EMAIL_LABEL}
              </Badge>
            )}
            {coach.location && (
              <Group gap="xs">
                <MapPin size={14} />
                <Text size="sm">{coach.location.split(',')[0]}</Text>
              </Group>
            )}
            {coach.home_base_timezone && (
              <Group gap="xs">
                <Clock size={14} />
                <Text size="sm">{formatTimezone(coach.home_base_timezone)}</Text>
              </Group>
            )}
          </Group>
        </Container>
      </Box>

      {/* Main content */}
      <Container size="xl" py="xl">
        <Grid gutter="lg">
        {/* Left Column - 70% */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="lg">
            {/* Basic Information */}
            <BasicInfoSection
              coach={coach}
              onSaved={handleCoachUpdate}
              editMedleyEmailRequest={editMedleyEmailRequest}
            />

            {/* Location & Timezone */}
            <Card className={classes.profileCard}>
              <Group justify="space-between" mb="md">
                <Text className={classes.cardTitle}>Location & Timezone</Text>
                <ActionIcon variant="subtle" onClick={openLocation}>
                  <Edit size={16} />
                </ActionIcon>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <Box>
                  <Text className={classes.fieldLabel}>Location</Text>
                  <Text className={classes.fieldContent}>{coach.location || '—'}</Text>
                </Box>
                <Box>
                  <Text className={classes.fieldLabel}>Home Base Timezone</Text>
                  <Text className={classes.fieldContent}>{coach.home_base_timezone || '—'}</Text>
                </Box>
                <Box>
                  <Text className={classes.fieldLabel}>Current Timezone Preference</Text>
                  <Text className={classes.fieldContent}>
                    {coach.timezone_preference || '—'}
                  </Text>
                </Box>
              </SimpleGrid>
            </Card>

            {/* Session Settings */}
            <Card className={classes.profileCard}>
              <Group justify="space-between" mb="md">
                <Text className={classes.cardTitle}>Session Settings</Text>
                <ActionIcon variant="subtle" onClick={openSessionSettings}>
                  <Edit size={16} />
                </ActionIcon>
              </Group>
              {!coach.zoom_link && !coach.teams_link && !coach.google_meet_link ? (
                <Text c="dimmed" size="sm">No meeting links configured</Text>
              ) : (
                <Stack gap="sm">
                  {coach.zoom_link && (
                    <Group gap="xs">
                      <Video size={16} />
                      <Text className={classes.fieldLabel} style={{ marginBottom: 0 }}>Zoom:</Text>
                      <Text
                        component="a"
                        href={coach.zoom_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                        c="blue"
                        style={{ textDecoration: 'none' }}
                      >
                        {coach.zoom_link}
                      </Text>
                      <ExternalLink size={12} />
                    </Group>
                  )}
                  {coach.zoom_passcode && (
                    <Group gap="xs">
                      <Lock size={16} />
                      <Text className={classes.fieldLabel} style={{ marginBottom: 0 }}>Zoom Passcode:</Text>
                      <Text size="sm">{coach.zoom_passcode}</Text>
                    </Group>
                  )}
                  {coach.teams_link && (
                    <Group gap="xs">
                      <Monitor size={16} />
                      <Text className={classes.fieldLabel} style={{ marginBottom: 0 }}>Teams:</Text>
                      <Text
                        component="a"
                        href={coach.teams_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                        c="blue"
                        style={{ textDecoration: 'none' }}
                      >
                        {coach.teams_link}
                      </Text>
                      <ExternalLink size={12} />
                    </Group>
                  )}
                  {coach.google_meet_link && (
                    <Group gap="xs">
                      <Globe size={16} />
                      <Text className={classes.fieldLabel} style={{ marginBottom: 0 }}>Google Meet:</Text>
                      <Text
                        component="a"
                        href={coach.google_meet_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                        c="blue"
                        style={{ textDecoration: 'none' }}
                      >
                        {coach.google_meet_link}
                      </Text>
                      <ExternalLink size={12} />
                    </Group>
                  )}
                </Stack>
              )}
            </Card>

            {/* Identity & Demographics - Now expanded by default */}
            <Card className={classes.profileCard}>
              <Group justify="space-between" mb="md">
                <Text className={classes.cardTitle}>Identity & Demographics</Text>
                <ActionIcon variant="subtle" onClick={openIdentity}>
                  <Edit size={16} />
                </ActionIcon>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <Box>
                  <Text className={classes.fieldLabel}>Birthday</Text>
                  <Text className={classes.fieldContent}>
                    {coach.birthday ? formatDateOnly(coach.birthday) : '—'}
                  </Text>
                </Box>
                <Box>
                  <Text className={classes.fieldLabel}>Pronouns</Text>
                  <Text className={classes.fieldContent}>
                    {coach.pronouns || '—'}
                  </Text>
                </Box>
                <Box>
                  <Text className={classes.fieldLabel}>Gender Identity</Text>
                  <Text className={classes.fieldContent}>
                    {coach.gender_identity || '—'}
                  </Text>
                </Box>
                <Box>
                  <Text className={classes.fieldLabel}>Race/Ethnicity</Text>
                  <Text className={classes.fieldContent}>
                    {coach.race_ethnicity?.length
                      ? coach.race_ethnicity.join(', ')
                      : '—'}
                  </Text>
                </Box>
                {coach.additional_identity_notes && (
                  <Box style={{ gridColumn: '1 / -1' }}>
                    <Text className={classes.fieldLabel}>Additional Notes</Text>
                    <Text className={classes.fieldContent}>{coach.additional_identity_notes}</Text>
                  </Box>
                )}
              </SimpleGrid>
            </Card>

            {/* Professional Background */}
            <Card className={classes.profileCard}>
              <Group justify="space-between" mb="md">
                <Text className={classes.cardTitle}>Professional Background</Text>
                <ActionIcon variant="subtle" onClick={openProfessional}>
                  <Edit size={16} />
                </ActionIcon>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <Box>
                  <Text className={classes.fieldLabel}>Industry Experience</Text>
                  <Text className={classes.fieldContent}>
                    {coach.industry_experience?.length
                      ? coach.industry_experience.join(', ')
                      : '—'}
                  </Text>
                </Box>
                <Box>
                  <Text className={classes.fieldLabel}>Facilitator Experience</Text>
                  <Text className={classes.fieldContent}>{coach.facilitator_experience ? 'Yes' : 'No'}</Text>
                </Box>
                <Box>
                  <Text className={classes.fieldLabel}>Certifications</Text>
                  <Text className={classes.fieldContent}>
                    {coach.certifications?.length
                      ? coach.certifications.join(', ')
                      : '—'}
                  </Text>
                </Box>
                {coach.expertise_areas?.length > 0 && (
                  <Box style={{ gridColumn: '1 / -1' }}>
                    <Text className={classes.fieldLabel}>Expertise Areas</Text>
                    <Group gap="xs" mt={4}>
                      {coach.expertise_areas.map((area) => (
                        <Badge key={area} variant="light" size="sm">
                          {area}
                        </Badge>
                      ))}
                    </Group>
                  </Box>
                )}
              </SimpleGrid>
            </Card>

            {/* Learning & Development */}
            <Card className={classes.profileCard}>
              <Group justify="space-between" mb="md">
                <Text className={classes.cardTitle}>Learning & Development</Text>
                <ActionIcon variant="subtle" onClick={openLearning}>
                  <Edit size={16} />
                </ActionIcon>
              </Group>
              <Stack gap="md">
                <Box>
                  <Text className={classes.fieldLabel}>Modality Proficiencies</Text>
                  {coach.modality_proficiencies?.length ? (
                    <Group gap="xs" mt={4}>
                      {coach.modality_proficiencies.map((m) => {
                        const isApproved = (coach.modality_approvals || []).includes(m);
                        return (
                          <Tooltip
                            key={m}
                            label={isApproved ? 'Medley Approved' : 'Not Approved'}
                            withArrow
                          >
                            <Badge
                              size="sm"
                              variant="light"
                              color="medleyGreen"
                              leftSection={isApproved ? <CheckCircle size={10} /> : undefined}
                            >
                              {m === 'group_coaching' ? 'Group Coaching' : m === 'one_on_one' ? '1:1 Coaching' : m === 'workshop' ? 'Workshop' : m}
                            </Badge>
                          </Tooltip>
                        );
                      })}
                    </Group>
                  ) : (
                    <Text className={classes.fieldContent}>No modalities assigned</Text>
                  )}
                </Box>
                <Box>
                  <Text className={classes.fieldLabel}>Preferred Learning Experiences</Text>
                  <Text className={classes.fieldContent}>
                    {coach.preferred_learning_experiences?.length
                      ? coach.preferred_learning_experiences.join(', ')
                      : '—'}
                  </Text>
                </Box>
                <Box>
                  <Text className={classes.fieldLabel}>Topics of Interest</Text>
                  <Text className={classes.fieldContent}>
                    {coach.coaching_topics_of_interest || '—'}
                  </Text>
                </Box>
                <Box>
                  <Text className={classes.fieldLabel}>Topics Wished Medley Addressed</Text>
                  <Text className={classes.fieldContent}>
                    {coach.topics_wished_medley_addressed || '—'}
                  </Text>
                </Box>
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>

        {/* Right Column - 30% */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="lg">
            {/* Connections Card (AC8a-8c) — Medley admins/CMs only */}
            <ConnectionsCard
              coachUserId={coach.user_id}
              coachName={`${coach.first_name} ${coach.last_name}`}
              coachEmail={coach.email}
              calendlyConnectedAt={coach.calendly_connected_at}
              calendlyBookingHealth={coach.calendly_booking_health}
              missingMeetingLinks={coach.missing_meeting_links}
            />

            {/* Stats */}
            <Card className={classes.profileCard}>
              <Text className={classes.cardTitle} mb="md">Stats</Text>
              <SimpleGrid cols={2} spacing="md">
                <Box ta="center">
                  <Text size="2rem" fw={700} c="medleyGreen">
                    {coach.active_cohort_count}
                  </Text>
                  <Text className={classes.fieldLabel} ta="center">Active Cohorts</Text>
                </Box>
                <Box ta="center">
                  <Text size="2rem" fw={700} c="medleyGreen">
                    {cohorts.current.length + cohorts.past.length}
                  </Text>
                  <Text className={classes.fieldLabel} ta="center">Total Cohorts</Text>
                </Box>
              </SimpleGrid>
            </Card>

            {/* Cohort History */}
            <Card className={classes.profileCard}>
              <Text className={classes.cardTitle} mb="md">Cohort History</Text>
              {cohorts.current.length === 0 && cohorts.past.length === 0 ? (
                <Text c="dimmed" size="sm" ta="center" py="md">
                  No cohort assignments yet
                </Text>
              ) : (
                <Stack gap="xs">
                  {cohorts.current.map((cohort) => (
                    <Box
                      key={cohort.cohort_id}
                      className={classes.cohortItem}
                      style={{ borderLeftColor: 'var(--mantine-color-green-6)' }}
                    >
                      <Text size="sm" fw={500}>
                        {cohort.cohort_name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {cohort.partner_name}
                      </Text>
                    </Box>
                  ))}
                  {cohorts.past.map((cohort) => (
                    <Box
                      key={cohort.cohort_id}
                      className={classes.cohortItem}
                      style={{ borderLeftColor: 'var(--mantine-color-gray-4)' }}
                    >
                      <Text size="sm" fw={500} c="dimmed">
                        {cohort.cohort_name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {cohort.partner_name}
                      </Text>
                    </Box>
                  ))}
                </Stack>
              )}
            </Card>

            {/* Activity Log */}
            <Card className={classes.profileCard}>
              <Text className={classes.cardTitle} mb="md">Activity Log</Text>
              {activityLog.length === 0 ? (
                <Text c="dimmed" size="sm" ta="center" py="md">
                  No activity yet
                </Text>
              ) : (
                <Timeline active={activityLog.length - 1} bulletSize={12} lineWidth={2}>
                  {activityLog.slice(0, 10).map((activity) => (
                    <Timeline.Item key={activity.id}>
                      <Text size="sm">{activity.description}</Text>
                      <Text size="xs" c="dimmed">
                        {formatDateTime(activity.created_at)}
                        {activity.changed_by_name && ` by ${activity.changed_by_name}`}
                      </Text>
                    </Timeline.Item>
                  ))}
                </Timeline>
              )}
            </Card>

            {/* System Information */}
            <Card className={classes.profileCard}>
              <Text className={classes.cardTitle} mb="md">System Information</Text>
              <Stack gap="xs">
                <Box>
                  <Text className={classes.fieldLabel}>Created</Text>
                  <Text className={classes.fieldContent}>{formatDateTime(coach.created_at)}</Text>
                </Box>
                <Box>
                  <Text className={classes.fieldLabel}>Last Updated</Text>
                  <Text className={classes.fieldContent}>{formatDateTime(coach.updated_at)}</Text>
                </Box>
                {coach.onboarded_at && (
                  <Box>
                    <Text className={classes.fieldLabel}>Onboarded</Text>
                    <Text className={classes.fieldContent}>{formatDateTime(coach.onboarded_at)}</Text>
                  </Box>
                )}
              </Stack>
            </Card>

            {/* Internal Notes */}
            <Card className={classes.profileCard}>
              <Group justify="space-between" mb="xs">
                <Text className={classes.cardTitle}>Internal Notes</Text>
                <ActionIcon variant="subtle" onClick={openNotes}>
                  <Edit size={16} />
                </ActionIcon>
              </Group>
              <Text size="xs" c="dimmed" mb="md">
                Only visible to Medley staff
              </Text>
              <RichTextContent
                content={coach.notes}
                emptyText="No internal notes yet"
                className={classes.fieldContent}
              />
            </Card>
          </Stack>
        </Grid.Col>
        </Grid>
      </Container>

      {/* Edit Modals */}
      <EditLocationTimezoneModal
        opened={locationOpened}
        onClose={closeLocation}
        coach={coach}
        onSaved={handleCoachUpdate}
      />
      <EditIdentityModal
        opened={identityOpened}
        onClose={closeIdentity}
        coach={coach}
        onSaved={handleCoachUpdate}
      />
      <EditProfessionalModal
        opened={professionalOpened}
        onClose={closeProfessional}
        coach={coach}
        onSaved={handleCoachUpdate}
      />
      <EditLearningModal
        opened={learningOpened}
        onClose={closeLearning}
        coach={coach}
        onSaved={handleCoachUpdate}
      />
      <EditSessionSettingsModal
        opened={sessionSettingsOpened}
        onClose={closeSessionSettings}
        coach={coach}
        onSaved={handleCoachUpdate}
      />
      <EditNotesModal
        opened={notesOpened}
        onClose={closeNotes}
        coach={coach}
        onSaved={handleCoachUpdate}
      />
      {/* Inactive Confirmation Modal */}
      <Modal
        opened={inactiveConfirmOpened}
        onClose={closeInactiveConfirm}
        title="Set Coach as Inactive?"
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            This coach is currently assigned to {cohorts.current.length} active{' '}
            {cohorts.current.length === 1 ? 'cohort' : 'cohorts'} with upcoming sessions.
            Setting them as inactive may affect scheduled sessions.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={closeInactiveConfirm}>
              Cancel
            </Button>
            <Button color="dangerRed" onClick={handleConfirmInactive} loading={changingStatus}>
              Set Inactive
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export default CoachDetailPage;
